import Keycloak from 'keycloak-js';
import { sentry } from '@/utils/sentry';
import { TRANSLATION_CONST } from '../../utils/translation';
import { localStorage } from '@/utils/local-storage';
import { Store } from 'vuex';
export interface KeyCloakConfig {
  clientId: string;
  realm: string;
  url: string;
  credentials?: {
    secret: string;
  };
}

type KeycloakTokenParsed = {
  realm_access: {
    roles: string[];
  };
};

export const KC_REFRESH_TOKEN = 'kcRefreshToken';
export const KC_TOKEN = 'kcToken';
export const KC_ID_TOKEN = 'kcIdToken';
export const KC_CLIENT_ID = 'kcClientId';

export class KeycloakProvider {
  private keycloak: typeof Keycloak;
  constructor(config: string | KeyCloakConfig, _?: boolean) {
    this.keycloak = new Keycloak(config);
  }

  private storeToken() {
    localStorage.set(KC_TOKEN, this.keycloak.token!);
    localStorage.set(KC_REFRESH_TOKEN, this.keycloak.refreshToken!);
    localStorage.set(KC_ID_TOKEN, this.keycloak.idToken!);
    localStorage.set(KC_CLIENT_ID, this.keycloak.clientId!);
  }

  public async init(responseMode: 'query' | 'fragment' = 'query', onLoad?: 'login-required' | 'check-sso') {
    const refreshToken = localStorage.get(KC_REFRESH_TOKEN) || '';
    const token = localStorage.get(KC_TOKEN) || '';
    const idToken = localStorage.get(KC_ID_TOKEN) || '';
    const clientId = localStorage.get(KC_CLIENT_ID) || '';

    this.keycloak.onAuthRefreshError = async () => {
      // eslint-disable-next-line no-console
      console.info('Error while refreshing token, logging out');
      this.keycloak.idToken = idToken;
      this.keycloak.clientId = clientId;
      await this.logout();
    };

    try {
      const isAuthenticated = await this.keycloak.init({
        refreshToken,
        token,
        responseMode,
        onLoad,
      });

      if (isAuthenticated) {
        this.storeToken();
        // It must be set in advance, otherwise automatic update won't work.
        this.updateTokenOnExpire();
        await this.updateToken(-1);
      }
    } catch (e) {
      sentry.captureException(e);
      // User switched KC clients
    }
  }

  public get isAuthenticated(): boolean | undefined {
    return this.keycloak.authenticated;
  }

  public async logout(store?: Store<any>, options?: { redirectUri: string }) {
    sentry.setUser(null);
    store?.dispatch('users/setB2BAuthClient', false);
    localStorage.remove(KC_REFRESH_TOKEN);
    localStorage.remove(KC_TOKEN);
    localStorage.remove(TRANSLATION_CONST);

    return this.keycloak.logout(options);
  }

  public login(mode: 'sn' | 'dn' | 'b2c' = 'sn') {
    if (mode === 'dn') {
      return window.location.replace(`/login/?action=business-dn`);
    } else if (mode === 'b2c') {
      window.location.replace(`/login/?action=business&redirect_uri=${window.encodeURIComponent(window.location.href)}`);
    }
  }

  public get tokenParsed(): KeycloakTokenParsed | undefined {
    return this.keycloak.tokenParsed;
  }

  public get companyIds() {
    return (this.keycloak.tokenParsed! as any).company_ids;
  }

  public get token(): string | undefined {
    return this.keycloak.token;
  }

  public hasRealmRole(role: string): boolean {
    return this.keycloak.hasRealmRole(role);
  }

  public hasResourceRole(role: string, resource?: string) {
    return this.keycloak.hasResourceRole(role, resource);
  }

  public loadUserProfile() {
    return this.keycloak.loadUserProfile();
  }

  public isTokenExpired(minValidity: number = 5): boolean {
    return this.keycloak.isTokenExpired(minValidity);
  }

  public onReady(cb: (auth: boolean) => void) {
    this.keycloak.onReady = cb;
  }

  public onAuthSuccess(cb: () => void) {
    this.keycloak.onAuthSuccess = cb;
  }

  public onAuthError = (cb: (errorData: any) => void) => {
    this.keycloak.onAuthError = cb;
  };

  private async updateToken(minValidity: number = 5) {
    const updatedToken = await this.keycloak.updateToken(minValidity);
    if (updatedToken) {
      this.storeToken();
    }
  }

  private updateTokenOnExpire() {
    this.keycloak.onTokenExpired = async () => {
      try {
        await this.updateToken(-1);
      } catch (_) {
        await this.logout();
      }
    };
  }
}
